/** @jsx jsx */
import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useContext } from 'react';
import { Drawer, Button, IDrawerProps } from '@blueprintjs/core';
import { DOCUMENT_END_POINTS, get } from 'srs-middleware';
import { WorkspaceContext } from "views/workspace/workspaceContext";
import { WorkspaceActionCreator } from "hooks/useWorkspaceState";
import { useAsyncCallback } from "hooks/useAsyncCallback";
import LoadingIcon from "components/loadingIcon";
import { ColorAliases } from 'styles/variables';
import { GenericObject } from 'interfaces/GenericObject';
import { ScrollBox } from 'components/scrollBox';
import { DefaultDocumentHeader, DocumentHeaderProps } from './documentHeader';
import { DefaultDocumentContent, DocumentContentProps } from './documentContent';

export interface DocumentViewerProps extends IDrawerProps, DocumentHeaderProps, DocumentContentProps {
	/** renders the item with subtitle content */
	subTitleRenderer?: React.FunctionComponent<{ item: GenericObject<any> }>,
	/** Renders the Header, fixed */
	headerRenderer?: React.FunctionComponent<DocumentHeaderProps>,
	/** Renders the body/content in a ScrollBox */
	contentRenderer?: React.FunctionComponent<DocumentContentProps>,
	/** TODO: support supportBookmarking */
	// supportBookmarking?: boolean,
}

/** Fetches and displays the active/selected document */
export const DocumentViewer: React.FunctionComponent<DocumentViewerProps> = ({
	titleProperty,
	bodyProperties,
	linkProperty,
	subTitleRenderer,
	headerRenderer: HeaderComponent = DefaultDocumentHeader,
	contentRenderer: ContentComponent = DefaultDocumentContent,
	singleColumn,
	hideEmptyValues,
	isOpen,
	onClose,
	size = 550,
	// supportBookmarking = true,
	...props
}) => {
	const {
		state: {
			explorer: {
				views: { documentViewer },
			},
		},
		dispatch,
	} = useContext(WorkspaceContext);

	let selectedDocumentId = documentViewer?.selectedDocumentId;
	isOpen = isOpen ?? !!selectedDocumentId; // @someoneSmarter: is this okay in React?

	onClose = onClose ?? useCallback(() => { // @someoneSmarter: is this okay in React?
		dispatch(
			WorkspaceActionCreator.updateViewProperties('documentViewer', null)
		);
	}, [dispatch]);

	const {
		data: item,
		loading
	}: {
		data: GenericObject<any>,
		loading: boolean
	} = useAsyncCallback(async () => {
		if (selectedDocumentId != null) {
			return await get(DOCUMENT_END_POINTS.GetDocumentById(selectedDocumentId));
		}
	}, [selectedDocumentId]);

	const {
		state: {
			explorer: {
				filters: {
					search: { terms, fields } = {
						terms: [],
						fields: [],
					},
				},
			},
		},
	} = useContext(WorkspaceContext);

	return (
		<Drawer {...props} isOpen={isOpen} onClose={onClose} size={size}>
			{/* TODO: skeleton instead of spinner? */}
			{loading && <LoadingIcon />}

			{!loading && item && (
				<React.Fragment>
					<ButtonContainer>
						<Button onClick={onClose} icon={'cross'} minimal cy-test="document-viewer-close-button" />
					</ButtonContainer>

					<HeaderComponent
						{...{ item, titleProperty, linkProperty, terms }}
						subTitle={subTitleRenderer && <SubTitleBody>{subTitleRenderer({ item })}</SubTitleBody>}
					/>

					<ScrollBox>
						<ContentComponent {...{ item, terms, fields, bodyProperties, singleColumn, hideEmptyValues }} />
					</ScrollBox>
				</React.Fragment>
			)}
		</Drawer>
	);
};
const ButtonContainer = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	margin: 1rem;
`;
const SubTitleBody = styled.p`
	color: ${ColorAliases.PT_TEXT_COLOR_MUTED};
`;
